// src/views/Warehouse.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback, useLayoutEffect } from 'react';
import { FaSlidersH, FaDove, FaShoePrints, FaBarcode } from 'react-icons/fa';
import Split from 'react-split';
import './Warehouse.css';
import { showToast, hideToast, updateToast } from '../utils/toast';
import Warehouse3D from './Warehouse3D';

// Walk icon uses react-icons FaShoePrints

// Bird icon for Observer Walk uses react-icons FaDove

const API = import.meta.env.VITE_API_URL;
// Reuse the same LS keys used by Designer so selections sync across views
const WH_PICK_LS   = 'wd_selected_warehouse_v1';
const AREA_PICK_LS = 'wd_selected_area_v2';
const AREA_BY_WH_LS  = 'wd_selected_area_by_wh_v1'; // keep in sync with WarehouseDesigner

const pad3 = n => (n == null || n === '' ? '' : String(n).padStart(3, '0'));
const pad2 = n => (n == null || n === '' ? '' : String(n).padStart(2, '0'));
const LOCATION_TYPE_OPTIONS = [
  { id: '1', label: 'FLOOR' },
  { id: '2', label: 'RACK' },
  { id: '3', label: 'PICKING' },
];
const locationTypeLabels = Array.from(new Set(LOCATION_TYPE_OPTIONS.map(o => o.label)));
const typeLabel = (value) => {
  const key = String(value ?? '').trim();
  const found = LOCATION_TYPE_OPTIONS.find(o => o.id === key);
  if (found) return found.label;
  if (key) return `TYPE ${key}`;
  return 'UNKNOWN';
};

const COLS_LS_KEY       = 'wh_table_col_widths_v1';
const RANGE_LS_KEY      = 'wh_range_v1';              // persist the range inputs
const INNER_SPLIT_LS_KEY = 'wh_inner_split_v1';       // persist left(list)/right(3D) split
const SHOW3D_LS_KEY     = 'wh_show_3d_v1';            // persist Show 3D toggle
const DESIGNER_AUTOSAVE_KEY = 'wh_designer_autosave_v4';

// ---- Default sort helpers: Aisle -> Bay -> Shelve -> Slot (numeric ASC) ----
const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
};
const cmp = (a, b) => (a === b ? 0 : a < b ? -1 : 1);
const defaultSort = (arr) =>
  [...(Array.isArray(arr) ? arr : [])].sort((a, b) =>
    cmp(toNum(a.roww),    toNum(b.roww))    ||
    cmp(toNum(a.columnn), toNum(b.columnn)) ||
    cmp(toNum(a.shelve),  toNum(b.shelve))  ||
    cmp(toNum(a.bin),     toNum(b.bin))
  );

// helper to load persisted range safely
const loadRangeFromLS = () => {
  try {
    const s = localStorage.getItem(RANGE_LS_KEY);
    if (!s) return null;
    const o = JSON.parse(s);
    return {
      aisleFrom: o?.aisleFrom ?? 1,
      aisleTo:   o?.aisleTo   ?? 10,
      bayFrom:   o?.bayFrom   ?? 1,
      bayTo:     o?.bayTo     ?? 10,
    };
  } catch {
    return null;
  }
};

// selection helpers
const getRowId = (r) => {
  if (r?.locationid != null) return r.locationid;
  if (r?.palletid != null) return `pallet:${r.palletid}`;
  return `${r?.roww}|${r?.columnn}|${r?.shelve}|${r?.bin}`;
};

const resolveLocationId = (r) => (
  r?.locationid ?? r?.locationId ?? r?.location_id ?? null
);

const stripClientFromPalletInfo = (raw) => {
  if (!raw) return '';
  const parts = String(raw).split(/\s*\|\s*/g).map(s => s.trim()).filter(Boolean);
  const cleaned = parts.map((entry) => {
    const segs = entry.split(' · ').map(s => s.trim()).filter(Boolean);
    // Backend formats each entry as "... · <client>", so drop the last segment.
    if (segs.length >= 2) segs.pop();
    return segs.join(' · ');
  });
  return cleaned.join(' | ');
};

const parseClientNames = (value) => (
  String(value || '')
    .split(',')
    .map(part => part.trim())
    .filter(Boolean)
);

const rowClientDisplay = (r) => {
  const single = String(r?.clientname || '').trim();
  if (single) return single;
  return String(r?.clients || '').trim();
};

const rowClientMatches = (r, selected) => {
  const want = String(selected || '').trim();
  if (!want) return true;
  const combo = [r?.clientname, r?.clients].filter(Boolean).join(',');
  const tokens = new Set(parseClientNames(combo).map(v => v.toLowerCase()));
  return tokens.has(want.toLowerCase());
};

export default function Warehouse() {
  // Filters / controls
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState(() => {
    try { return String(localStorage.getItem(WH_PICK_LS) || ''); } catch { return ''; }
  });
  const [areas, setAreas] = useState([]);
  const [areaId, setAreaId] = useState(() => {
    try {
      const v = localStorage.getItem(AREA_PICK_LS);
      return v != null ? String(v) : '1';
    } catch { return '1'; }
  });
  const [show3D, setShow3D] = useState(() => {
    try {
      const v = localStorage.getItem(SHOW3D_LS_KEY);
      return v == null ? true : (v === '1' || v === 'true');
    } catch {
      return true;
    }
  });

  // initialize range inputs from localStorage (fallbacks provided)
  const persistedRange = loadRangeFromLS() || {};
  const [aisleFrom, setAisleFrom] = useState(persistedRange.aisleFrom ?? 1);
  const [aisleTo,   setAisleTo]   = useState(persistedRange.aisleTo   ?? 10);
  const [bayFrom,   setBayFrom]   = useState(persistedRange.bayFrom   ?? 1);
  const [bayTo,     setBayTo]     = useState(persistedRange.bayTo     ?? 10);

  const [whDims, setWhDims] = useState({ lengthFt: 2000, widthFt: 1200, heightFt: 32 });
  // track inner split sizes so top controls can match left-pane width (persisted)
  const [innerSizes, setInnerSizes] = useState(() => {
    try {
      const raw = localStorage.getItem(INNER_SPLIT_LS_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length === 2 && parsed.every(n => Number.isFinite(n) && n > 0)) {
        const sum = parsed[0] + parsed[1];
        if (Math.abs(sum - 100) > 0.1) {
          const f = 100 / sum;
          return [parsed[0] * f, parsed[1] * f];
        }
        return parsed;
      }
    } catch {}
    return [65, 35];
  });

  // Persist range inputs whenever they change
  useEffect(() => {
    const payload = { aisleFrom, aisleTo, bayFrom, bayTo };
    try { localStorage.setItem(RANGE_LS_KEY, JSON.stringify(payload)); } catch {}
  }, [aisleFrom, aisleTo, bayFrom, bayTo]);

  // Persist inner split sizes whenever they change
  useEffect(() => {
    try { localStorage.setItem(INNER_SPLIT_LS_KEY, JSON.stringify(innerSizes)); } catch {}
  }, [innerSizes]);

  // Persist Show 3D toggle
  useEffect(() => {
    try { localStorage.setItem(SHOW3D_LS_KEY, show3D ? '1' : '0'); } catch {}
  }, [show3D]);

  // Data
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [floorRows, setFloorRows] = useState([]);
  const [floorLoading, setFloorLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('');
  const [aisleFilter, setAisleFilter] = useState('');
  const [bayFilter, setBayFilter] = useState('');
  const [shelveFilter, setShelveFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [sortState, setSortState] = useState({ key: '', dir: 'asc' }); // dir: 'asc' | 'desc'

  const show3DEffective = show3D && typeFilter !== 'FLOOR';
  const tableLoading = typeFilter === 'FLOOR' ? floorLoading : loading;
  const activeRows = typeFilter === 'FLOOR' ? floorRows : rows;
  const activeRowCount = Array.isArray(activeRows) ? activeRows.length : 0;

  // Centered topbar toast; on completion, report count and keep a moment
  const prevLoadingRef = useRef(false);
  useEffect(() => {
    const id = 'loading.warehouse.locations';
    if (tableLoading && !prevLoadingRef.current) {
      showToast({ id, message: 'Loading locations…', duration: 0, align: 'center', slideFrom: 'top', pill: true });
    }
    if (!tableLoading && prevLoadingRef.current) {
      const formatted = Number(activeRowCount).toLocaleString('en-US');
      updateToast({ id, message: `Loaded ${formatted} locations.` });
      const t = setTimeout(() => hideToast(id), 2500);
      return () => clearTimeout(t);
    }
    prevLoadingRef.current = tableLoading;
  }, [tableLoading, activeRowCount]);
  const [loadProgress, setLoadProgress] = useState(0);

  // Row selection (for highlight) + 3D ref (to focus camera)
  const [selectedKey, setSelectedKey] = useState('');
  const selectedRowElRef = useRef(null); // direct DOM highlight when 3D is hidden
  const w3dRef = useRef(null);

  // ---------- NEW: checkboxes selection state ----------
  const [checkedSet, setCheckedSet] = useState(() => new Set()); // none checked initially

  const uniqueValues = (key, numeric = false) => {
    const values = Array.from(new Set(rows.map(r => String(r[key] ?? '').trim()).filter(Boolean)));
    if (!numeric) {
      values.sort((a, b) => a.localeCompare(b));
      return values;
    }
    return values.sort((a, b) => {
      const na = Number(a);
      const nb = Number(b);
      if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
      if (Number.isFinite(na)) return -1;
      if (Number.isFinite(nb)) return 1;
      return a.localeCompare(b);
    });
  };

  const clientOptions = useMemo(() => {
    const base = (typeFilter === 'FLOOR') ? floorRows : rows;
    const names = new Set();
    for (const r of (Array.isArray(base) ? base : [])) {
      const combo = [r?.clientname, r?.clients].filter(Boolean).join(',');
      for (const name of parseClientNames(combo)) names.add(name);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [typeFilter, floorRows, rows]);

  const typeOptions = useMemo(() => locationTypeLabels, []);
  const aisleOptions = useMemo(() => uniqueValues('roww', true), [rows]);
  const bayOptions = useMemo(() => uniqueValues('columnn', true), [rows]);
  const shelveOptions = useMemo(() => uniqueValues('shelve', true), [rows]);

  const displayedRows = useMemo(() => {
    if (typeFilter === 'FLOOR') {
      return (Array.isArray(floorRows) ? floorRows : []).filter((row) => rowClientMatches(row, clientFilter));
    }
    return rows.filter((row) => {
      const matchesType = !typeFilter || typeLabel(row.type) === typeFilter;
      const matchesAisle = !aisleFilter || String(row.roww ?? '').trim() === aisleFilter;
      const matchesBay = !bayFilter || String(row.columnn ?? '').trim() === bayFilter;
      const matchesShelve = !shelveFilter || String(row.shelve ?? '').trim() === shelveFilter;
      const matchesClient = rowClientMatches(row, clientFilter);
      return matchesType && matchesAisle && matchesBay && matchesShelve && matchesClient;
    });
  }, [rows, floorRows, typeFilter, aisleFilter, bayFilter, shelveFilter, clientFilter]);

  const sortedDisplayedRows = useMemo(() => {
    const list = Array.isArray(displayedRows) ? [...displayedRows] : [];
    const dir = sortState.dir === 'desc' ? -1 : 1;
    const key = String(sortState.key || '').trim();
    if (!key) return list;

    const normText = (v) => String(v ?? '').trim().toLowerCase();
    const normNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
    };

    const valueFor = (row) => {
      switch (key) {
        case 'client': return normText(rowClientDisplay(row));
        case 'pallet': return normText(stripClientFromPalletInfo(row?.palletInfo ?? row?.palletinfo ?? ''));
        case 'type': return normText(typeLabel(row?.type));
        case 'aisle': return normNum(row?.roww);
        case 'bay': return normNum(row?.columnn);
        default: return '';
      }
    };

    list.sort((a, b) => {
      const av = valueFor(a);
      const bv = valueFor(b);
      if (typeof av === 'number' && typeof bv === 'number') {
        if (av === bv) return 0;
        return (av < bv ? -1 : 1) * dir;
      }
      return String(av).localeCompare(String(bv)) * dir;
    });
    return list;
  }, [displayedRows, sortState]);
  const displayedRowIds = useMemo(() => sortedDisplayedRows.map(getRowId), [sortedDisplayedRows]);
  const allChecked = displayedRowIds.length > 0 && displayedRowIds.every(id => checkedSet.has(id));
  const someChecked = displayedRowIds.length > 0 && displayedRowIds.some(id => checkedSet.has(id)) && !allChecked;
  const headerCheckRef = useRef(null);

  // reflect indeterminate state in native checkbox
  useEffect(() => {
    if (headerCheckRef.current) {
      headerCheckRef.current.indeterminate = someChecked;
    }
  }, [someChecked, allChecked]);

  useEffect(() => {
    setCheckedSet(new Set());
  }, [sortedDisplayedRows]);

  // Animate an indeterminate progress bar while locations are loading
  useEffect(() => {
    if (!tableLoading) {
      if (loadProgress > 0 && loadProgress < 100) setLoadProgress(100);
      const t = setTimeout(() => setLoadProgress(0), 250);
      return () => clearTimeout(t);
    }
    setLoadProgress(p => (p > 0 ? p : 20));
    const id = setInterval(() => {
      setLoadProgress(prev => {
        if (!tableLoading) return prev;
        const next = prev + Math.random() * 12;
        return Math.min(90, next);
      });
    }, 180);
    return () => clearInterval(id);
  }, [tableLoading]);


  // whenever warehouseId changes, fetch dims
  useEffect(() => {
    if (!warehouseId) return;
    (async () => {
      try {
        // Prefer the latest Designer snapshot dims (if present) so 2D and 3D match immediately.
        try {
          const raw = localStorage.getItem(DESIGNER_AUTOSAVE_KEY);
          const snap = raw ? JSON.parse(raw) : null;
          if (snap && String(snap.warehouseId) === String(warehouseId) && snap.dims) {
            const Ls = Number(snap.dims.lengthFt);
            const Ws = Number(snap.dims.widthFt);
            const Hs = Number(snap.dims.heightFt);
            if (Number.isFinite(Ls) && Number.isFinite(Ws)) {
              setWhDims({
                lengthFt: Ls,
                widthFt: Ws,
                heightFt: Number.isFinite(Hs) ? Hs : 32
              });
            }
          }
        } catch {}

        const row = await fetch(`${API}/api/warehouses/${encodeURIComponent(warehouseId)}?v=${Date.now()}`, { cache: 'no-store' }).then(r => r.json());

        setWhDims({
          lengthFt: Number(row.length) || 2000,
          widthFt:  Number(row.width)  || 1200,
          heightFt: Number(row.height) || 32
        });
      } catch {
        // Keep the large default scale so object positions don't appear wildly offset
        // if the dims request fails momentarily.
        setWhDims({ lengthFt: 2000, widthFt: 1200, heightFt: 32 });
      }
    })();
  }, [warehouseId]);

  // --- Topbar title (set after render; clear on unmount) ---
  useEffect(() => {
    const count = activeRowCount;
    const subtitle = count > 0 ? `(${count.toLocaleString()} locations)` : '';
    window.dispatchEvent(new CustomEvent('topbar:setTitle', {
      detail: { title: 'Warehouse', subtitle }
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('topbar:clearTitle'));
    };
  }, [warehouseId, activeRowCount]);

  const toggleAll = (checked) => {
    if (checked) {
      setCheckedSet(new Set(displayedRowIds));
    } else {
      setCheckedSet(new Set());
    }
  };

  const toggleOne = (id, checked) => {
    setCheckedSet(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };
  // -----------------------------------------------------

  // ----- Table columns (resizable + persisted widths) -----
  const COLUMNS = useMemo(
    () => [
      // Renamed and repurposed: selection column
      { key: 'select',   title: 'All',      defaultWidth: 36,  align: 'center' },
      { key: 'action',   title: 'Location Actions',  defaultWidth: 140, align: 'center' },
      { key: 'client',   title: 'Client',   defaultWidth: 160 },
      { key: 'pallet',   title: 'Pallet Info', defaultWidth: 300 },
      { key: 'type',     title: 'Type',        defaultWidth: 72 },
      { key: 'aisle',    title: 'Aisle',       defaultWidth: 72 },
      { key: 'bay',      title: 'Bay',         defaultWidth: 72 },
      { key: 'shelve',   title: 'Shelve',      defaultWidth: 64 },
      { key: 'slot',     title: 'Slot',        defaultWidth: 64 },
      { key: 'stack',    title: 'Stack',       defaultWidth: 72 },
    ],
    []
  );

  const [colWidths, setColWidths] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(COLS_LS_KEY) || '{}') || {};
      return saved;
    } catch {
      return {};
    }
  });

  // Per-column minimum width based on header text measurement
  const headerRefs = useRef({});
  const [minColWidths, setMinColWidths] = useState({});

  const actionButtonsCount = (typeFilter === 'FLOOR')
    ? 0
    : (show3DEffective ? 3 : 1);
  const actionColWidthPx = useMemo(() => {
    // Approximate the width needed by the rendered action buttons (icons + padding + gaps).
    // Match Receipts-style "mini-btn" sizing (tight icon-only buttons).
    const BTN_W = 24;
    const GAP = 0;
    const PAD = 8;
    if (actionButtonsCount <= 0) return 10;
    return (actionButtonsCount * BTN_W) + ((actionButtonsCount - 1) * GAP) + PAD;
  }, [actionButtonsCount]);
  const actionColMinPx = useMemo(() => {
    // Ensure the header ("Location Actions") is always readable, even when there are no buttons (FLOOR).
    const headerMin = Number(minColWidths?.action || 0);
    return Math.max(80, headerMin);
  }, [minColWidths]);

  // Columns visible in the table (hide Actions when 3D is hidden)
  const visibleColumns = COLUMNS;

  const setHeaderRef = (key) => (el) => {
    headerRefs.current[key] = el || undefined;
  };

  useLayoutEffect(() => {
    const PAD_LR = 20;  // match .th-inner padding + buffer
    const HANDLE_W = 8; // resizer handle width
    const next = {};
    for (const col of visibleColumns) {
      const container = headerRefs.current[col.key];
      if (!container) continue;
      let width;
      if (col.key === 'select') {
        // No text label; allow a smaller minimum to keep checkbox usable
        width = 28;
      } else {
        const label = container.querySelector('.th-label') || container;
        const rect = label.getBoundingClientRect();
        width = Math.ceil(rect.width) + PAD_LR + HANDLE_W;
      }
      next[col.key] = Math.max(28, width);
    }
    setMinColWidths(next);
  }, [visibleColumns]);

  useEffect(() => {
    const onResize = () => {
      const PAD_LR = 20;
      const HANDLE_W = 8;
      const next = {};
      for (const col of visibleColumns) {
        const container = headerRefs.current[col.key];
        if (!container) continue;
        let width;
        if (col.key === 'select') {
          width = 28;
        } else {
          const label = container.querySelector('.th-label') || container;
          const rect = label.getBoundingClientRect();
          width = Math.ceil(rect.width) + PAD_LR + HANDLE_W;
        }
        next[col.key] = Math.max(28, width);
      }
      setMinColWidths(next);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [visibleColumns]);

  const persistWidths = (next) => {
    setColWidths(next);
    try { localStorage.setItem(COLS_LS_KEY, JSON.stringify(next)); } catch {}
  };

  const startResize = (colKey, ev) => {
    ev.preventDefault();
    ev.stopPropagation();

    const startX = ev.clientX;
    // Measure from the actual column header width to mirror Receipts behavior
    const th = ev.currentTarget?.closest ? ev.currentTarget.closest('th') : null;
    const measuredW = th?.offsetWidth;
    const colDef = COLUMNS.find(c => c.key === colKey);
    const startW = Number.isFinite(measuredW) && measuredW > 0
      ? measuredW
      : (colWidths[colKey] ?? colDef?.defaultWidth ?? 120);

    // Visual/UX hint: show resize cursor everywhere while dragging
    try { document.body.classList.add('col-resizing'); } catch {}

    const onMove = (e) => {
      const delta = e.clientX - startX;
      // Allow resizing down to a small global minimum; no header-based min
      const newW = Math.max(10, Math.min(800, startW + delta));
      persistWidths({ ...colWidths, [colKey]: newW });
    };
    const onUp = () => {
      try { document.body.classList.remove('col-resizing'); } catch {}
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const setSortKey = (key) => {
    const k = String(key || '').trim();
    if (!k) return;
    setSortState((prev) => {
      if (prev.key === k) {
        return { key: k, dir: prev.dir === 'asc' ? 'desc' : 'asc' };
      }
      return { key: k, dir: 'asc' };
    });
  };

  const widthFor = (key) => {
    if (key === 'select') return '36px'; // keep checkbox column minimal and fixed
    if (key === 'action') return `${Math.max(actionColWidthPx, actionColMinPx)}px`; // only as wide as buttons OR header
    const colDef = COLUMNS.find(c => c.key === key);
    return (colWidths[key] ?? colDef?.defaultWidth ?? 120) + 'px';
  };

  // Applied width for any column (fixed table layout):
  const appliedWidth = (key) => widthFor(key);

  const minWidthFor = (key) => {
    const v = minColWidths[key];
    return ((v != null ? Math.max(10, v) : 10)) + 'px';
  };

  // --- fetch helper ---
  const fetchJSON = async (url, opts) => {
    const res = await fetch(url, opts);
    if (!res.ok) {
      let msg = `${res.status} ${res.statusText}`;
      try {
        const b = await res.json();
        if (b?.error) msg = b.error;
      } catch {}
      throw new Error(msg);
    }
    return res.json();
  };

  // --- Warehouses + default ---
  useEffect(() => {
    (async () => {
      try {
        const list = await fetchJSON(`${API}/api/warehouses`);
        const opts = Array.isArray(list) ? list.map(w => ({ id: w.id, name: w.name })) : [];
        setWarehouses(opts);
        if (!warehouseId && opts.length) {
          // fall back to first if nothing persisted
          setWarehouseId(String(opts[0].id));
        }
      } catch {
        setWarehouses([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist warehouse selection
  useEffect(() => {
    if (warehouseId) {
      try { localStorage.setItem(WH_PICK_LS, String(warehouseId)); } catch {}
    }
  }, [warehouseId]);

  // --- Areas for selected warehouse ---
  useEffect(() => {
    if (!warehouseId) return;
    (async () => {
      try {
        const data = await fetchJSON(`${API}/api/warehouseareas/warehouseareas?warehouseid=${warehouseId}`);
        const arr = Array.isArray(data) ? data : [];
        setAreas(arr);
        // Prefer per-warehouse mapping (matches WarehouseDesigner), then current, then legacy global.
        const isValid = (id) => id != null && arr.some(a => String(a.id) === String(id));
        let mapped = null;
        try {
          const m = JSON.parse(localStorage.getItem(AREA_BY_WH_LS) || '{}') || {};
          mapped = m && m[String(warehouseId)];
        } catch {}
        const legacy = (() => { try { return localStorage.getItem(AREA_PICK_LS); } catch { return null; } })();
        const id1 = arr.find(a => String(a.id) === '1')?.id;
        const first = arr[0]?.id;
        const nextArea = !arr.length
          ? '1'
          : (isValid(mapped)
              ? String(mapped)
              : (isValid(areaId)
                  ? String(areaId)
                  : (isValid(legacy)
                      ? String(legacy)
                      : String(id1 ?? first ?? '1'))));
        if (String(areaId ?? '') !== String(nextArea)) setAreaId(String(nextArea));
      } catch {
        setAreas([]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId]);

  // Persist area selection
  useEffect(() => {
    try { localStorage.setItem(AREA_PICK_LS, String(areaId ?? '')); } catch {}
    try {
      if (warehouseId) {
        const raw = localStorage.getItem(AREA_BY_WH_LS) || '{}';
        const m = JSON.parse(raw) || {};
        m[String(warehouseId)] = String(areaId ?? '');
        localStorage.setItem(AREA_BY_WH_LS, JSON.stringify(m));
      }
    } catch {}
  }, [areaId, warehouseId]);

  // --- Load range (explicit via Refresh / Apply) ---
  const loadRange = async () => {
    if (!warehouseId || !areaId) return;

    // sanitize numbers
    const aFrom = Math.max(1, Number(aisleFrom) || 1);
    const aTo   = Math.max(aFrom, Number(aisleTo) || aFrom);
    const bFrom = Math.max(1, Number(bayFrom) || 1);
    const bTo   = Math.max(bFrom, Number(bayTo) || bFrom);

    setLoading(true);
    try {
      const qs = new URLSearchParams();

      // required
      qs.set('warehouseid', String(warehouseId));
      qs.set('areaid', String(areaId));

      // canonical params
      qs.set('aisleStart', String(aFrom));
      qs.set('aisleEnd',   String(aTo));
      qs.set('bayStart',   String(bFrom));
      qs.set('bayEnd',     String(bTo));

      // compatibility aliases
      qs.set('row_from', String(aFrom));
      qs.set('row_to',   String(aTo));
      qs.set('aisle_from', String(aFrom));
      qs.set('aisle_to',   String(aTo));
      qs.set('fromrow',  String(aFrom));
      qs.set('torow',    String(aTo));
      qs.set('rowstart', String(aFrom));
      qs.set('rowend',   String(aTo));
      qs.set('startrow', String(aFrom));
      qs.set('endrow',   String(aTo));

      qs.set('bay_from', String(bFrom));
      qs.set('bay_to',   String(bTo));
      qs.set('column_from', String(bFrom));
      qs.set('column_to',   String(bTo));
      qs.set('frombay',  String(bFrom));
      qs.set('tobay',    String(bTo));
      qs.set('columnstart', String(bFrom));
      qs.set('columnend',   String(bTo));
      qs.set('startbay', String(bFrom));
      qs.set('endbay',   String(bTo));

      const data = await fetchJSON(`${API}/api/locations/range-with-pallets?${qs.toString()}`);
      // 👉 Enforce default sort regardless of server order
      setRows(defaultSort(Array.isArray(data) ? data : []));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFloorPallets = useCallback(async () => {
    if (!warehouseId || !areaId) {
      setFloorRows([]);
      return;
    }
    setFloorLoading(true);
    setFloorRows([]);
    try {
      const qs = new URLSearchParams();
      qs.set('warehouseid', String(warehouseId));
      qs.set('areaid', String(areaId));
      const data = await fetchJSON(`${API}/api/locations/floor-pallets?${qs.toString()}`);
      setFloorRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('GET /api/locations/floor-pallets error', err);
      setFloorRows([]);
    } finally {
      setFloorLoading(false);
    }
  }, [warehouseId, areaId]);

  useEffect(() => {
    if (typeFilter === 'FLOOR') {
      loadFloorPallets();
    }
  }, [typeFilter, loadFloorPallets]);

  // Initial load (warehouse/area change)
  useEffect(() => {
    if (warehouseId) loadRange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, areaId]);

  // ---- Row click => focus 3D camera ----
  const onRowClick = (r, ev) => {
    const aisle = Number(r.roww);
    const bay   = Number(r.columnn);
    const shelf = Number(r.shelve) || 1;
    const bin = Number(r.bin);

    // Do not zoom when 3D view is hidden
    if (!show3DEffective) {
      // Avoid full table re-render; toggle class directly for snappy UI
      try {
        const tr = ev?.currentTarget?.closest('tr');
        if (selectedRowElRef.current && selectedRowElRef.current !== tr) {
          selectedRowElRef.current.classList.remove('selected');
        }
        if (tr) {
          tr.classList.add('selected');
          selectedRowElRef.current = tr;
        }
      } catch {}
      return;
    }

    if (Number.isFinite(aisle) && Number.isFinite(bay)) {
      w3dRef.current?.focusLocation({ aisle, bay, shelf, bin });
      setSelectedKey(`${aisle}|${bay}|${shelf}|${r.bin ?? ''}`);
    }
  };

  // ---- Render helpers ----
  const renderCell = (r, key) => {
    switch (key) {
      case 'select': {
        const id = getRowId(r);
        const checked = checkedSet.has(id);
        return (
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => toggleOne(id, e.target.checked)}
            onClick={(e) => e.stopPropagation()}
          />
        );
      }
      case 'client': {
        return rowClientDisplay(r);
      }
      case 'pallet': {
        // supports either "WR123 · Pallet #5 · Client | WR..." or multi-line from backend
        const raw = stripClientFromPalletInfo((r.palletInfo ?? r.palletinfo ?? '').toString());
        if (!raw.trim()) return '';
        const text = raw.replace(/\s*\|\s*/g, '\n'); // " | " -> newline
        return <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>;
      }
      case 'action': {
        if (typeFilter === 'FLOOR') return '';
        return (
          <div className="actions">
            <button
              className="mini-btn"
              aria-label="Print location barcode"
              title="Print location barcode"
              onClick={(e) => {
                e.stopPropagation();
                const locationId = resolveLocationId(r);
                if (!locationId) {
                  showToast({
                    id: 'warehouse-print-error',
                    message: 'Unable to print barcode: location ID missing',
                    type: 'error',
                    align: 'center',
                    pill: true,
                  });
                  return;
                }
                const url = `${API}/api/locations/labels?ids=${encodeURIComponent(locationId)}&size=4x6&sym=pdf417&v=${Date.now()}`;
                window.open(url, '_blank', 'noopener,noreferrer');
              }}
            >
              <FaBarcode size={16} />
            </button>
            {show3DEffective && (
              <>
                <button
                  className="mini-btn"
                  aria-label="Walk to this location"
                  title="Walk to this location"
                  onClick={(e) => {
                    e.stopPropagation();
                    const a = Number(r.roww), b = Number(r.columnn);
                    const shelf = Number(r.shelve) || 1;
                    const bin = Number(r.bin) || null;
                    w3dRef.current?.walkToLocation({ aisle: a, bay: b, shelf, bin });
                  }}
                >
                  <FaShoePrints size={16} />
                </button>
                <button
                  className="mini-btn"
                  aria-label="Observer view walk"
                  title="Observer view walk"
                  onClick={(e) => {
                    e.stopPropagation();
                    const a = Number(r.roww), b = Number(r.columnn);
                    const shelf = Number(r.shelve) || 1;
                    const bin = Number(r.bin) || null;
                    w3dRef.current?.setObserverMode(true);
                    w3dRef.current?.walkToLocation({ aisle: a, bay: b, shelf, bin });
                  }}
                >
                  <FaDove size={16} />
                </button>
              </>
            )}
          </div>
        );
      }
      case 'type':
        return typeLabel(r.type);
      case 'aisle':
        return pad3(r.roww);
      case 'bay':
        return pad3(r.columnn);
      case 'shelve':
        return pad2(r.shelve);
      case 'slot':
        return pad2(r.bin);
      case 'stack':
        // Show comma-separated distinct stacks (server populates r.stacks; fallback to r.stack)
        return (r.stacks && String(r.stacks).trim()) ? String(r.stacks) : (r.stack ?? '');
      case 'legacy':
        return `${r.roww ?? ''}-${r.columnn ?? ''}${r.shelve != null ? '-' + r.shelve : ''}${r.bin != null ? '-' + r.bin : ''}`;
      default:
        return r[key] ?? '';
    }
  };

  // ---- Controls markup ----
  const Controls = (
    <div className="controls-wrap">
      <div className="controls-grid" style={{ width: (show3DEffective ? innerSizes[0] : 100) + '%', maxWidth: '100%' }}>
        <fieldset className="wh-fieldset section-warehouse">
          <legend>Which Warehouse</legend>
          <div className="wh-row">
            <div className="wh-field">
              <label>Select warehouse to view</label>
              <select value={warehouseId} onChange={e => setWarehouseId(e.target.value)}>
                {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
            <div className="wh-field">
              <label>Select area</label>
              <select value={areaId} onChange={e => setAreaId(e.target.value)}>
                {areas.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
          </div>
        </fieldset>

        <fieldset className="wh-fieldset section-aisle">
          <legend>Aisle Range</legend>
          <div className="wh-row">
            <div className="wh-field">
              <label>Starting Aisle</label>
              <input
                type="number"
                min={1}
                value={aisleFrom}
                onChange={e => setAisleFrom(e.target.value)}
              />
            </div>
            <div className="wh-field">
              <label>Ending Aisle</label>
              <input
                type="number"
                min={aisleFrom || 1}
                value={aisleTo}
                onChange={e => setAisleTo(e.target.value)}
              />
            </div>
          </div>
        </fieldset>

        <fieldset className="wh-fieldset section-bay">
          <legend>Bay Range</legend>
          <div className="wh-row">
            <div className="wh-field">
              <label>Starting Bay</label>
              <input
                type="number"
                min={1}
                value={bayFrom}
                onChange={e => setBayFrom(e.target.value)}
              />
            </div>
            <div className="wh-field">
              <label>Ending Bay</label>
              <input
                type="number"
                min={bayFrom || 1}
                value={bayTo}
                onChange={e => setBayTo(e.target.value)}
              />
            </div>
          </div>
        </fieldset>
        <div className="controls-apply section-apply">
          <button className="btn primary" onClick={loadRange}>
            <FaSlidersH className="btn-icon" />
            Apply Range
          </button>
        </div>
      </div>
    </div>
  );

  // ---- Table markup (with sticky header & resizers) ----
  const Table = (
    <div className="table-shell">
      <table className="wh-table">
        <colgroup>
          {visibleColumns.map((c, idx) => {
            const isLast = idx === visibleColumns.length - 1;
            // Leave last column flexible (auto) so it soaks up slack
            return (
              <col key={c.key} style={isLast ? undefined : { width: widthFor(c.key) }} />
            );
          })}
        </colgroup>
        <thead>
          <tr>
            {visibleColumns.map(col => (
              <th
                key={col.key}
                style={undefined}
              >
                {col.key === 'select' ? (
                  <div
                    className="th-inner"
                    ref={setHeaderRef(col.key)}
                    style={{
                      textAlign: 'center',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: '4px 4px' // minimal padding so 36px fits the checkbox comfortably
                    }}
                  >
                    <input
                      ref={headerCheckRef}
                      type="checkbox"
                      checked={allChecked}
                      onChange={(e) => toggleAll(e.target.checked)}
                      onClick={(e) => e.stopPropagation()}
                      title="Select / deselect all rows"
                    />
                  </div>
                ) : (
                  <div
                    ref={setHeaderRef(col.key)}
                    className="th-inner"
                    style={{ textAlign: col.align || 'left', cursor: (['client','pallet','type','aisle','bay'].includes(col.key) ? 'pointer' : 'default') }}
                    onClick={() => {
                      if (['client','pallet','type','aisle','bay'].includes(col.key)) setSortKey(col.key);
                    }}
                    title={['client','pallet','type','aisle','bay'].includes(col.key) ? 'Click to sort' : undefined}
                  >
                    <span className="th-label">
                      {col.key === 'action' ? <>Location<br />Actions</> : col.title}
                      {sortState.key === col.key ? (sortState.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </span>
                    {/* Drag handle */}
                    {col.key !== 'action' && (
                      <span
                        className="col-resizer"
                        onMouseDown={(e) => startResize(col.key, e)}
                        onClick={(e) => e.stopPropagation()}
                        title="Drag to resize"
                      />
                    )}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {tableLoading ? (
            <tr><td colSpan={visibleColumns.length}>Loading…</td></tr>
          ) : sortedDisplayedRows.length ? (
            sortedDisplayedRows.map(r => {
              const rowKey = `${r.roww}|${r.columnn}|${r.shelve}|${r.bin}`;
              const id = getRowId(r);
              const isChecked = checkedSet.has(id);
              return (
                <tr
                  key={id}
                  className={`clickable ${selectedKey === rowKey ? 'selected' : ''}`}
                  onClick={(e) => onRowClick(r, e)}
                  title={show3DEffective ? "Go to this location in 3D" : "Select row"}
                >
                  {visibleColumns.map(c => (
                    <td
                      key={c.key}
                      className={c.key === 'pallet' ? 'pallet-lines' : (c.key === 'action' ? 'td-actions' : '')}
                      style={(() => {
                        const base = {
                          textAlign: c.align || (c.key === 'select' ? 'center' : 'left'),
                          verticalAlign: c.key === 'select' ? 'middle' : (c.key === 'in_use' ? 'middle' : 'top'),
                        };
                        if (c.key === 'select') {
                          base.padding = '0 4px';
                        }
                        // Widths are controlled via <colgroup>; avoid overriding per-cell widths
                        return base;
                      })()}
                      onClick={(c.key === 'select' || c.key === 'action') ? (e) => e.stopPropagation() : undefined}
                    >
                      {c.key === 'select' ? (
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => toggleOne(id, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        renderCell(r, c.key)
                      )}
                    </td>
                  ))}
                </tr>
              );
            })
          ) : (
            <tr><td colSpan={visibleColumns.length}>No locations found.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="warehouse-wrap">
      {/* OUTER split: top controls / bottom content */}
      <Split
        className="warehouse-outer-split"
        direction="vertical"
        sizes={[24, 76]}
        minSize={[120, 260]}
        gutterSize={8}
      >
        {/* TOP: controls */}
        <div className="controls-pane">
          {Controls}
          <div className="controls-view-toggle" style={{ marginTop: '0.5rem' }}>
            <label
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: typeFilter === 'FLOOR' ? 'not-allowed' : 'pointer' }}
              title={typeFilter === 'FLOOR' ? '3D view is unavailable for FLOOR pallets.' : ''}
            >
              <input
                type="checkbox"
                checked={show3DEffective}
                disabled={typeFilter === 'FLOOR'}
                onChange={(e) => setShow3D(e.target.checked)}
              />
              Show 3D view
            </label>
          </div>
        </div>

        {/* BOTTOM: LEFT table | RIGHT 3D (keep Split mounted; collapse 3D when hidden) */}
        <Split
          className={`warehouse-inner-split ${show3DEffective ? '' : 'hide-3d'}`}
          direction="horizontal"
          sizes={show3DEffective ? innerSizes : [100, 0]}
          onDrag={(s) => setInnerSizes(s)}
          onDragEnd={(s) => setInnerSizes(s)}
          minSize={[320, show3DEffective ? 360 : 0]}
          gutterSize={show3DEffective ? 8 : 0}
        >
          <div className="locations-table-pane">
            {loadProgress > 0 && (
              <div style={{ position: 'sticky', top: 0, zIndex: 7, background: 'transparent', padding: '6px 0 8px 0' }}>
                <div style={{ height: 6, width: `${loadProgress}%`, background: '#21bb50', borderRadius: 4, boxShadow: '0 0 6px rgba(33,187,80,0.35)', transition: 'width 180ms ease' }} />
              </div>
            )}
            <div className="row-filters">
              <label>
                <span>Type</span>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                  <option value="">All</option>
                  {typeOptions.map(opt => (
                    <option key={`type-${opt}`} value={opt}>{opt}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Client</span>
                <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}>
                  <option value="">All</option>
                  {clientOptions.map(opt => (
                    <option key={`client-${opt}`} value={opt}>{opt}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Aisle</span>
                <select value={aisleFilter} onChange={(e) => setAisleFilter(e.target.value)}>
                  <option value="">All</option>
                  {aisleOptions.map(opt => (
                    <option key={`aisle-${opt}`} value={opt}>{pad3(opt)}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Bay</span>
                <select value={bayFilter} onChange={(e) => setBayFilter(e.target.value)}>
                  <option value="">All</option>
                  {bayOptions.map(opt => (
                    <option key={`bay-${opt}`} value={opt}>{pad3(opt)}</option>
                  ))}
                </select>
              </label>
              <label>
                <span>Shelve</span>
                <select value={shelveFilter} onChange={(e) => setShelveFilter(e.target.value)}>
                  <option value="">All</option>
                  {shelveOptions.map(opt => (
                    <option key={`shelve-${opt}`} value={opt}>{pad2(opt)}</option>
                  ))}
                </select>
              </label>
            </div>
            {Table}
          </div>
          <div className="three-pane fill">
            {/* Mount 3D only when visible to avoid unnecessary render loop */}
            {show3DEffective ? (
              <Warehouse3D
                ref={w3dRef}
                rows={rows}
                warehouseId={warehouseId}
                areaId={areaId}
                dims={whDims}
                origin="upper-left"   // match WarehouseDesigner (0,0 at NW/top-left)
              />
            ) : null}
          </div>
        </Split>
      </Split>
    </div>
  );
}
